import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import EmptyState from '@/components/base/EmptyState';
import ShareDialog from '@/components/base/ShareDialog';
import { getNewsById, formatDate } from '@/lib/queries';
import { newsList } from '@/mocks/news';
import { useSeo } from '@/hooks/useSeo';

export default function NewsDetailPage() {
  const { id } = useParams<{ id: string }>();
  const article = getNewsById(id ?? '');
  const [shareOpen, setShareOpen] = useState(false);

  useSeo({
    title: article ? `${article.title} - 日韩成人电影资讯` : '资讯不存在 - 日韩成人电影',
    description: article ? article.summary : '该资讯条目不存在，请返回资讯列表浏览其他内容。',
    keywords: article ? `${article.tags.join(',')},${article.section},日韩成人电影资讯` : '日韩成人电影资讯',
    image: article?.cover,
    type: 'article',
  });

  if (!article) {
    return (
      <div className="mx-auto max-w-content px-4 py-16 md:px-6 md:py-24">
        <EmptyState
          icon="ri-article-line"
          title="没有找到这篇资讯"
          description="文章可能已经调整或删除。你可以返回资讯列表，查看当前最新发布的内容。"
          primaryLabel="返回资讯列表"
          primaryTo="/news"
        />
      </div>
    );
  }

  const related = newsList.filter((item) => item.id !== article.id && item.section === article.section).slice(0, 3);
  const fallbackRelated = newsList.filter((item) => item.id !== article.id).slice(0, 3);
  const recommend = related.length ? related : fallbackRelated;
  const paragraphs = article.body.filter((block) => block.type === 'p');
  const readMinutes = Math.max(3, Math.round(paragraphs.reduce((sum, block) => sum + (block.text?.length ?? 0), 0) / 320));

  return (
    <>
      <section className="border-b border-background-800/80 bg-gradient-to-b from-primary-950/40 via-background-950 to-background-950">
        <div className="mx-auto max-w-content px-4 pb-8 pt-6 md:px-6 md:pb-10 md:pt-8">
          <Breadcrumb items={[{ label: '影视资讯', to: '/news' }, { label: article.section, to: '/news' }, { label: article.title }]} />

          <div className="mt-6 max-w-3xl">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded bg-primary-600/90 px-2 py-1 text-[11px] text-background-50">{article.section}</span>
              <span className="font-label text-[11px] uppercase tracking-[0.16em] text-foreground-600">
                {formatDate(article.date)}
              </span>
              <span className="h-3 w-px bg-background-700" />
              <span className="text-[11px] text-foreground-500">{article.author}</span>
              <span className="h-3 w-px bg-background-700" />
              <span className="text-[11px] text-foreground-500">约 {readMinutes} 分钟阅读</span>
            </div>

            <h1 className="mt-4 text-2xl font-black leading-snug tracking-tight text-foreground-50 md:text-4xl">
              {article.title}
            </h1>
            <p className="mt-4 text-sm leading-relaxed text-foreground-400 md:text-[15px]">{article.summary}</p>

            <div className="mt-5 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => setShareOpen(true)}
                className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-share-forward-line text-sm" />
                分享这篇文章
              </button>
              <Link
                to="/news"
                className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-700 px-4 py-2 text-xs text-foreground-300 transition-colors hover:border-accent-500 hover:text-accent-400"
              >
                <i className="ri-arrow-left-line text-sm" />
                返回资讯列表
              </Link>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-content px-4 py-8 md:px-6 md:py-12">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[minmax(0,1fr)_300px] lg:gap-14">
          <article className="min-w-0">
            <div className="overflow-hidden rounded-lg border border-background-800 bg-background-900">
              <img
                src={article.cover}
                alt={`${article.title} 文章配图`}
                title={`${article.title} - 日韩成人电影资讯`}
                className="h-auto w-full object-cover object-top"
              />
            </div>

            <div className="mt-8 space-y-5">
              {article.body.map((block, index) => {
                if (block.type === 'h2') {
                  return (
                    <h2 key={index} className="pt-3 text-lg font-bold text-foreground-50 md:text-xl">
                      {block.text}
                    </h2>
                  );
                }
                if (block.type === 'img') {
                  return (
                    <figure key={index} className="overflow-hidden rounded-lg border border-background-800">
                      <img
                        src={block.src}
                        alt={block.caption ?? `${article.title} 配图`}
                        title={`${article.title} - 日韩成人电影资讯`}
                        className="h-auto w-full object-cover object-top"
                      />
                      {block.caption && (
                        <figcaption className="border-t border-background-800 bg-background-900 px-4 py-2.5 text-[11px] text-foreground-600">
                          {block.caption}
                        </figcaption>
                      )}
                    </figure>
                  );
                }
                return (
                  <p key={index} className="text-sm leading-[1.95] text-foreground-400 md:text-[15px]">
                    {block.text}
                  </p>
                );
              })}
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-2 border-t border-background-800 pt-6">
              <span className="text-xs text-foreground-600">文章标签：</span>
              {article.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/tag/${encodeURIComponent(tag)}`}
                  className="rounded-full border border-background-700 px-2.5 py-1 text-[11px] text-foreground-400 transition-colors hover:border-accent-500 hover:text-accent-400"
                >
                  # {tag}
                </Link>
              ))}
            </div>

            <div className="mt-8">
              <h2 className="text-lg font-bold text-foreground-50 md:text-xl">相关推荐</h2>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
                {recommend.map((item) => (
                  <Link
                    key={item.id}
                    to={`/news/${item.id}`}
                    className="group flex cursor-pointer flex-col overflow-hidden rounded-lg border border-background-800 bg-background-900 transition-colors hover:border-primary-600"
                  >
                    <div className="h-[120px] w-full overflow-hidden bg-background-800">
                      <img
                        src={item.cover}
                        alt={`${item.title} 配图`}
                        title={`${item.title} - 日韩成人电影资讯`}
                        className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="flex flex-1 flex-col p-3.5">
                      <span className="text-[10px] text-accent-500">{item.section}</span>
                      <h3 className="mt-1.5 line-clamp-2 text-xs font-medium leading-relaxed text-foreground-200 transition-colors group-hover:text-accent-400">
                        {item.title}
                      </h3>
                      <span className="mt-auto pt-2 font-label text-[10px] tracking-[0.12em] text-foreground-600">
                        {formatDate(item.date)}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </article>

          <aside className="w-full">
            <div className="sticky top-24 space-y-4">
              <div className="rounded-lg border border-background-800 bg-background-900 p-5">
                <h2 className="text-sm font-semibold text-foreground-100">文章信息</h2>
                <dl className="mt-4 space-y-3 text-xs">
                  <div className="flex items-center justify-between border-b border-background-800 pb-2.5">
                    <dt className="text-foreground-600">栏目</dt>
                    <dd className="text-foreground-200">{article.section}</dd>
                  </div>
                  <div className="flex items-center justify-between border-b border-background-800 pb-2.5">
                    <dt className="text-foreground-600">发布时间</dt>
                    <dd className="font-label tracking-wide text-foreground-200">{formatDate(article.date)}</dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-foreground-600">作者</dt>
                    <dd className="text-foreground-200">{article.author}</dd>
                  </div>
                </dl>
              </div>

              <div className="rounded-lg border border-background-800 bg-background-900 p-5">
                <h2 className="text-sm font-semibold text-foreground-100">继续浏览</h2>
                <div className="mt-4 space-y-2.5">
                  <Link
                    to="/news"
                    className="flex cursor-pointer items-center justify-between rounded-md border border-background-800 px-3 py-2.5 text-xs text-foreground-300 transition-colors hover:border-primary-600 hover:text-accent-400"
                  >
                    全部资讯
                    <i className="ri-arrow-right-s-line text-sm" />
                  </Link>
                  <Link
                    to="/category/drama"
                    className="flex cursor-pointer items-center justify-between rounded-md border border-background-800 px-3 py-2.5 text-xs text-foreground-300 transition-colors hover:border-primary-600 hover:text-accent-400"
                  >
                    剧情片分类
                    <i className="ri-arrow-right-s-line text-sm" />
                  </Link>
                  <Link
                    to="/search"
                    className="flex cursor-pointer items-center justify-between rounded-md border border-background-800 px-3 py-2.5 text-xs text-foreground-300 transition-colors hover:border-primary-600 hover:text-accent-400"
                  >
                    搜索影片
                    <i className="ri-arrow-right-s-line text-sm" />
                  </Link>
                </div>
              </div>

              <p className="px-1 text-[11px] leading-relaxed text-foreground-700">
                本文内容为演示用途撰写，涉及的人名、机构与数据均为虚构，不代表任何真实主体。
              </p>
            </div>
          </aside>
        </div>
      </div>

      <ShareDialog open={shareOpen} title={article.title} onClose={() => setShareOpen(false)} />
    </>
  );
}